import * as Sentry from "@sentry/vue";

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const route = useRoute();
  const {
    public: { sentry },
  } = useRuntimeConfig();

  if (!sentry.dsn) {
    return;
  }

  
  Sentry.init({
    dsn: "https://cedf4ad3b591ab202030e3801eed5fb3@o4507507741294592.ingest.de.sentry.io/4507507743326288",
    integrations: [
      Sentry.feedbackIntegration({
        // Disable auto-injection so we can control where it appears
        autoInject: false,
        
        // Appearance & Branding
        colorScheme: "system",
        showBranding: false, // Remove Sentry logo
        id: "superseeded-feedback", // Custom ID for CSS targeting
        outline: "rgba(155,155,155,0)",
        // User & Form Configuration
        showName: false,
        showEmail: false,
        useSentryUser: {
          name: "fullName",
          email: "email"
        },
        enableScreenshot: true,
        isNameRequired: false,
        isEmailRequired: false,
        // Text Customization
        triggerLabel: "",
        triggerAriaLabel: "Send feedback to SuperSeeded",
        formTitle: "Feedback",
        submitButtonLabel: "Send",
        cancelButtonLabel: "Cancel",
        confirmButtonLabel: "Confirm",
        addScreenshotButtonLabel: "Add Screenshot",
        removeScreenshotButtonLabel: "Remove Screenshot",
        nameLabel: "Your Name",
        namePlaceholder: "Enter your name",
        emailLabel: "Email Address",
        emailPlaceholder: "your.email@example.com",
        messageLabel: "Tell us what's on your mind",
        messagePlaceholder: "Describe your experience, report a bug, or suggest an improvement...",
        successMessageText: "Thanks so much for your feedback!",
        // Event Callbacks
        onFormOpen: () => {
          console.log('Feedback form opened')
        },
        onFormClose: () => {
          console.log('Feedback form closed')
        },
        onSubmitSuccess: (data: any) => {
          console.log('Feedback submitted successfully:', data)
        },
        onSubmitError: (error: Error) => {
          console.error('Feedback submission failed:', error)
        },
        isRequiredLabel: false,
        // // Custom theme using Tailwind CSS variables
        themeLight: {
          accentBackground: "hsl(var(--popover))",
          accentForeground: "hsl(var(--primary))",
          outline: "hsl(var(--accent))",
          
        //   background: "hsl(var(--background))",
        //   backgroundHover: "hsl(var(--muted))",
        //   foreground: "hsl(var(--foreground))",
        //   error: "hsl(var(--destructive))",
        //   success: "#10b981",
        //   border: "hsl(var(--border))",
        //   boxShadow: "var(--shadow-xxl)",
        //   submitBackground: "hsl(var(--primary))",
        //   submitBackgroundHover: "hsl(var(--primary))",
        //   submitForeground: "hsl(var(--primary-foreground))",
        //   cancelBackground: "hsl(var(--muted))",
        //   cancelBackgroundHover: "hsl(var(--accent))",
        //   cancelForeground: "hsl(var(--muted-foreground))",
        //   interactiveFilter: "brightness(95%)",
        },
        themeDark: {
          accentBackground: "hsl(var(--popover))",
          accentForeground: "hsl(var(--primary))",
          outline: "hsl(var(--accent))",
        //   background: "hsl(var(--background))",
        //   backgroundHover: "hsl(var(--muted))",
        //   foreground: "hsl(var(--foreground))",
        //   error: "hsl(var(--destructive))",
        //   success: "#34d399",
        //   border: "hsl(var(--border))",
        //   boxShadow: "var(--shadow-xxl)",
        //   submitBackground: "hsl(var(--primary))",
        //   submitBackgroundHover: "hsl(var(--primary))",
        //   submitForeground: "hsl(var(--primary-foreground))",
        //   cancelBackground: "hsl(var(--muted))",
        //   cancelBackgroundHover: "hsl(var(--accent))",
        //   cancelForeground: "hsl(var(--muted-foreground))",
        //   interactiveFilter: "brightness(105%)",
        },
        
        // Tags to add to feedback events
        tags: {
          source: 'superseeded-app',
          version: '1.0.0',
          environment: sentry.environment || 'development'
        }
      }),
    ],
  });

  // Global widget management - only runs once
  if (import.meta.client) {
    // Get the feedback instance
    const feedback = Sentry.getFeedback();
    
    // Use shared global state from composable
    const getGlobalState = () => (window as any).__sentryWidgetState || { instance: null, isVisible: false };

    const showWidget = () => {
      const state = getGlobalState();
      if (route.path === '/' || !feedback || state.isVisible) {
        return;
      }

      try {
        state.instance = feedback.createWidget();
        state.instance?.appendToDom();
        state.isVisible = true;
        console.log('Sentry feedback widget created globally for route:', route.path);
      } catch (error) {
        console.error('Failed to create Sentry widget globally:', error);
      }
    };

    const hideWidget = () => {
      if (!feedback) return;
      const state = getGlobalState();

      try {
        if (state.instance) {
          state.instance.removeFromDom?.();
          state.instance = null;
        }
        
        feedback.remove();
        
        const widgetElements = document.querySelectorAll('#superseeded-feedback, .sentry-feedback-widget, [id^="sentry-feedback"]');
        widgetElements.forEach(el => el.remove());
        
        state.isVisible = false;
        console.log('Sentry feedback widget hidden globally');
      } catch (error) {
        console.warn('Error hiding Sentry widget globally:', error);
      }
    };

    // Watch for route changes - only runs once globally
    watch(() => route.path, (newPath) => {
      const state = getGlobalState();
      if (newPath === '/') {
        hideWidget();
      } else if (!state.isVisible) {
        showWidget();
      }
    }, { immediate: true });
  }
  

  // Sentry.init({
  //   app: nuxtApp.vueApp,
  //   dsn: sentry.dsn,
  //   environment: sentry.environment,
  //   integrations: [
  //     Sentry.browserTracingIntegration({ router }),
  //     Sentry.replayIntegration({
  //       maskAllText: false,
  //       blockAllMedia: false,
  //     }),
  //   ],

  //   // Configure this whole part as you need it!

  //   tracesSampleRate: 0.2, // Change in prod

  //   // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  //   tracePropagationTargets: ["localhost", "https://superseeded.ai"],

  //   replaysSessionSampleRate: 1.0, // Change in prod
  //   replaysOnErrorSampleRate: 1.0, // Change in prod if necessary
  // });
});
